import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import { Heart, Star, ShieldCheck, Truck, Leaf, Droplets, Sun, Sprout, Thermometer, MessageCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [variantId, setVariantId] = useState(null);
  const [qty, setQty] = useState(1);
  const [pin, setPin] = useState(localStorage.getItem("yp_pin") || "");
  const [pinCheck, setPinCheck] = useState(null);
  const [checkingPin, setCheckingPin] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.product(id);
        setProduct(data);
        setRelated(data.related?.also_bought || []);
        // Prefer landing on a size that's actually in stock rather than blindly the first one.
        const defaultVariant = data.variants?.find((v) => v.stock > 0) ?? data.variants?.[0];
        setVariantId(defaultVariant?.variant_id || null);
        setQty(1);
        setActiveImg(0);
        window.scrollTo(0, 0);
      } catch (e) { console.error(e); }
    })();
  }, [id]);

  if (!product) return <div className="mx-auto max-w-7xl px-4 py-24 text-center text-[var(--yp-muted)]">Loading plant…</div>;

  const selectedVariant = product.variants?.find((v) => v.variant_id === variantId) || product.variants?.[0];
  const currentPrice = selectedVariant?.price ?? product.price;
  const currentMrp = selectedVariant?.mrp ?? product.mrp;
  const discount = currentMrp > currentPrice ? Math.round(((currentMrp - currentPrice) / currentMrp) * 100) : 0;
  const images = product.images && product.images.length ? product.images : ["/plant-placeholder.png"];
  // Gated on the selected size's own stock, not the product as a whole — one size can be sold
  // out while another isn't.
  const outOfStock = !(selectedVariant?.stock > 0);

  const checkPin = async () => {
    if (pin.length !== 6) { toast.error("Enter a valid 6-digit pincode"); return; }
    localStorage.setItem("yp_pin", pin);
    setCheckingPin(true);
    try {
      const res = await api.pincode(pin);
      setPinCheck(res);
    } catch {
      toast.error("Couldn't check delivery for this pincode");
      setPinCheck(null);
    } finally {
      setCheckingPin(false);
    }
  };

  const selectVariant = (v) => {
    setVariantId(v.variant_id);
    setQty((q) => (v.stock > 0 ? Math.min(q, v.stock) : q));
  };

  return (
    <div data-testid="product-detail" className="mx-auto max-w-7xl px-4 py-8 md:py-12">
      <Seo
        title={product.name}
        description={`Buy ${product.name} online at ${inr(currentPrice)} from YourPlants.in. ${product.description ? String(product.description).slice(0, 120) : "Quality plants delivered from trusted local nurseries with Pay After Check."}`}
        path={`/product/${id}`}
        image={images[0]}
      />
      <div className="text-xs text-[var(--yp-muted)] mb-4">
        <Link to="/" className="hover:underline">Home</Link> · <Link to={`/shop/${product.category}`} className="hover:underline capitalize">{product.category}</Link> · <span>{product.name}</span>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div>
          <div className="aspect-[4/5] bg-[var(--yp-surface-alt)] rounded-3xl overflow-hidden">
            <img src={images[activeImg]} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-3">
              {images.map((img, i) => (
                <button key={i} data-testid={`thumb-${i}`} onClick={() => setActiveImg(i)} className={`w-20 h-20 rounded-xl overflow-hidden border-2 ${i === activeImg ? "border-[var(--yp-primary)]" : "border-transparent"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <h1 className="font-serif-display text-3xl md:text-4xl text-[var(--yp-primary)]">{product.name}</h1>
          {product.scientific_name && <div className="italic text-sm text-[var(--yp-muted)] mt-1">{product.scientific_name}</div>}
          <div className="flex items-center gap-2 mt-3 text-sm">
            <div className="flex text-[var(--yp-accent)]"><Star size={16} className="fill-current" /></div>
            <span className="font-medium">{product.rating?.toFixed(1)}</span>
            <span className="text-[var(--yp-muted)]">· {product.review_count} reviews</span>
          </div>

          <div className="mt-6 flex items-baseline gap-3">
            <div className="text-3xl font-semibold text-[var(--yp-primary)]">{inr(currentPrice)}</div>
            {currentMrp > currentPrice && (<>
              <div className="text-base line-through text-[var(--yp-muted)]">{inr(currentMrp)}</div>
              <div className="chip !bg-[var(--yp-primary)] !text-white !border-[var(--yp-primary)]">Save {discount}%</div>
            </>)}
          </div>
          <div className="text-xs text-[var(--yp-muted)] mt-1">Inclusive of all taxes · Free shipping over ₹499</div>
          {outOfStock && (
            <div data-testid="out-of-stock-banner" className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-white bg-[var(--yp-error)] rounded-full px-3 py-1.5">
              Out of Stock
            </div>
          )}

          {product.variants?.length > 0 && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-[var(--yp-muted)] mb-2">Size</div>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v) => {
                  const sizeOutOfStock = !(v.stock > 0);
                  return (
                    <button
                      key={v.variant_id}
                      data-testid={`variant-${v.label.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => !sizeOutOfStock && selectVariant(v)}
                      disabled={sizeOutOfStock}
                      title={sizeOutOfStock ? `${v.label} — out of stock` : v.label}
                      className={`px-4 py-2 rounded-full border text-sm ${
                        sizeOutOfStock
                          ? "border-[var(--yp-border)] text-[var(--yp-muted)] opacity-50 cursor-not-allowed line-through"
                          : variantId === v.variant_id
                            ? "border-[var(--yp-primary)] bg-[var(--yp-primary)] text-white"
                            : "border-[var(--yp-border)] hover:border-[var(--yp-primary)]"
                      }`}
                    >
                      {v.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border border-[var(--yp-border)] rounded-full">
              <button data-testid="qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-2">−</button>
              <div data-testid="qty-value" className="px-3 min-w-[2ch] text-center">{qty}</div>
              <button
                data-testid="qty-increase"
                onClick={() => setQty((q) => (selectedVariant?.stock > 0 ? Math.min(q + 1, selectedVariant.stock) : q))}
                disabled={selectedVariant?.stock > 0 && qty >= selectedVariant.stock}
                className="px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                +
              </button>
            </div>
            <button
              data-testid="btn-add-to-cart"
              onClick={() => addItem(product, variantId, qty)}
              disabled={outOfStock}
              className="flex-1 btn-primary rounded-full py-3 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {outOfStock ? "Out of Stock" : `Add to Cart · ${inr(currentPrice * qty)}`}
            </button>
          </div>
          {outOfStock ? (
            <div className="mt-3 block text-center rounded-full border border-[var(--yp-border)] text-[var(--yp-muted)] py-3 text-sm font-medium cursor-not-allowed">
              Buy Now
            </div>
          ) : (
            <Link to="/cart" data-testid="btn-buy-now" onClick={() => addItem(product, variantId, qty)} className="mt-3 block text-center rounded-full border border-[var(--yp-primary)] text-[var(--yp-primary)] py-3 text-sm font-medium hover:bg-[var(--yp-primary)] hover:text-white">
              Buy Now
            </Link>
          )}

          {/* Pincode */}
          <div className="mt-6 border border-[var(--yp-border)] rounded-2xl p-4 flex items-center gap-3">
            <Truck size={18} className="text-[var(--yp-primary)]" />
            <input data-testid="pincode-check-input" placeholder="Delivery pincode" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} className="flex-1 outline-none bg-transparent text-sm" />
            <button data-testid="btn-check-pincode" onClick={checkPin} disabled={checkingPin} className="text-sm font-medium text-[var(--yp-primary)] hover:underline disabled:opacity-60">
              {checkingPin ? "Checking…" : "Check"}
            </button>
          </div>
          {pinCheck && (
            <div data-testid="pincode-result" className={`mt-2 text-xs ${pinCheck.available ? "text-[var(--yp-success)]" : "text-[var(--yp-error)]"}`}>
              {pinCheck.message}{pinCheck.city ? ` · ${pinCheck.city}, ${pinCheck.state}` : ""}
            </div>
          )}

          {/* Trust */}
          <div className="mt-6 grid grid-cols-3 gap-2 text-xs text-[var(--yp-muted)]">
            <div className="flex flex-col items-start gap-1"><ShieldCheck size={16} className="text-[var(--yp-primary)]" /><span>Healthy Plant Guarantee</span></div>
            <div className="flex flex-col items-start gap-1"><Leaf size={16} className="text-[var(--yp-primary)]" /><span>Nursery-fresh</span></div>
            <div className="flex flex-col items-start gap-1"><Heart size={16} className="text-[var(--yp-primary)]" /><span>Pay After Check</span></div>
          </div>

          <a data-testid="btn-bulk-enquiry" href={`/bulk?product=${encodeURIComponent(product.name)}`} className="mt-6 inline-flex items-center gap-2 text-sm text-[var(--yp-primary)] font-medium hover:underline">
            <MessageCircle size={16} /> Buying in bulk? Request a quote →
          </a>
        </div>
      </div>

      {/* Description & Care */}
      <div className="grid md:grid-cols-3 gap-8 mt-14">
        <div className="md:col-span-2">
          <h2 className="font-serif-display text-2xl text-[var(--yp-primary)]">About this plant</h2>
          <p className="mt-3 text-[var(--yp-text)] leading-relaxed">{product.description}</p>

          {(product.water || product.sunlight || product.soil || product.temperature) && (
            <>
              <h3 className="font-serif-display text-xl text-[var(--yp-primary)] mt-8">Care essentials</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                {product.water && <CareTile icon={Droplets} label="Water" value={product.water} />}
                {product.sunlight && <CareTile icon={Sun} label="Sunlight" value={product.sunlight} />}
                {product.soil && <CareTile icon={Sprout} label="Soil" value={product.soil} />}
                {product.temperature && <CareTile icon={Thermometer} label="Temperature" value={product.temperature} />}
              </div>
            </>
          )}
        </div>
        <div>
          <div className="bg-[var(--yp-surface-alt)] rounded-2xl p-5 border border-[var(--yp-border)]">
            <div className="font-serif-display text-lg text-[var(--yp-primary)]">Quick facts</div>
            <dl className="mt-3 text-sm">
              {product.height && <FactRow label="Height" value={product.height} />}
              {product.pot_size && <FactRow label="Pot size" value={product.pot_size} />}
              <FactRow label="Placement" value={product.indoor_outdoor} />
              {product.air_purifying && <FactRow label="Feature" value="Air-purifying" />}
              {product.pet_friendly && <FactRow label="Pet-friendly" value="Yes" />}
              {product.flowering && <FactRow label="Flowering" value="Yes" />}
              {product.fruiting && <FactRow label="Fruit-bearing" value="Yes" />}
            </dl>
          </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-16">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif-display text-2xl md:text-3xl text-[var(--yp-primary)]">You may also love</h2>
            <Link to={`/shop/${product.category}`} className="text-sm text-[var(--yp-primary)] hover:underline">See more <ArrowRight size={14} className="inline" /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {related.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function CareTile({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-[var(--yp-border)] p-4">
      <Icon size={18} className="text-[var(--yp-primary)]" />
      <div className="text-xs uppercase tracking-widest text-[var(--yp-muted)] mt-2">{label}</div>
      <div className="text-sm mt-1">{value}</div>
    </div>
  );
}

function FactRow({ label, value }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-[var(--yp-border)] last:border-0">
      <dt className="text-[var(--yp-muted)]">{label}</dt><dd className="font-medium">{value}</dd>
    </div>
  );
}
